/**
 * Locale Admin API Service — Override APIs
 *
 * Handles communication with the tms-api-locale backend for admin-only
 * (Localization Settings) operations:
 * - Browse locale keys         (GET /keys)            → ADMIN
 * - Batch save overrides       (PUT /override)        → ADMIN
 * - Revert a single override   (DELETE /override/:key)→ ADMIN + JWT
 * - Bulk CSV import            (POST /import)         → ADMIN
 * - Sync bundle from tms-types (POST /sync)           → ADMIN
 *
 * These are the "override APIs" used specifically in:
 *   • Settings > Localization > Terminology Editor
 *   • Onboarding > Terminology Review step
 *   • Admin CSV import workflow
 *
 * For default locale APIs (bundle loading, language listing),
 * see locale.service.ts.
 *
 * Backend: tms-api-locale (port 5011)
 * Gateway prefix: /locale → strips prefix → upstream /api/v1/locale/...
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  LocaleKey,
  LocaleKeysQuery,
  LocaleOverrideBody,
  LocaleOverrideBatchQuery,
  LocaleSyncBody,
} from '@tms-phoenix/tms-types';

// Re-export types for convenience
export type { LocaleKey, LocaleKeysQuery, LocaleOverrideBody, LocaleOverrideBatchQuery, LocaleSyncBody };

// ── API path ────────────────────────────────────────────────────────────────

/**
 * Full locale API prefix routed through the gateway.
 * Same prefix as locale.service.ts — shared across default + admin APIs.
 */
const LOCALE_API = `${API_ROUTES.LOCALE}/locale`;

/** Standard API response envelope. */
interface _ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: {
    requestId: string;
    timestamp: string;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

/** Response shape for PUT /override. */
interface OverrideResponse {
  processed: number;
  total: number;
  message: string;
  bundle: Record<string, string>;
}

/** Response shape for POST /import. */
export interface ImportResponse {
  processed: number;
  skipped: number;
  total: number;
  message: string;
  bundle: Record<string, string>;
}

/** Response shape for POST /sync. */
export interface SyncResponse {
  tenant_id: string;
  language_code: string;
  is_initial_setup: boolean;
  s3_key: string;
  db_rows_affected: number;
  total_keys: number;
}

// ── Override APIs (Localization Settings) ────────────────────────────────────

/**
 * GET /api/v1/locale/keys
 *
 * Paginated browser over the locale_strings table.
 * ADMIN role required (auth pending on BE).
 *
 * Used by:
 *   • Settings > Localization > Terminology Editor (key list with filters)
 *   • Onboarding > Terminology Review step (pre-filled form)
 *
 * Supports filtering by module, key_type, is_customized, and full-text search.
 *
 * @param query - Filter/pagination params (module, key_type, is_customized, search, page, limit)
 * @returns Paginated array of LocaleKey objects with pagination metadata
 */
export async function fetchLocaleKeys(query: LocaleKeysQuery = {}): Promise<{
  data: LocaleKey[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}> {
  const api = getApiClient();
  const params = new URLSearchParams();

  if (query.module) params.set('module', query.module);
  if (query.key_type) params.set('key_type', query.key_type);
  if (query.is_customized) params.set('is_customized', query.is_customized);
  if (query.search) params.set('search', query.search);
  if (query.lang) params.set('lang', query.lang);
  if (query.page) params.set('page', query.page);
  if (query.limit) params.set('limit', query.limit);

  const url = params.toString()
    ? `${LOCALE_API}/keys?${params.toString()}`
    : `${LOCALE_API}/keys`;

  // createApiClient response interceptor unwraps { success, data, meta }
  // → response.data = the inner `data` array
  // → response.pagination = meta.pagination (attached by interceptor)
  const response = await api.get(url);

  const pagination = (response as unknown as Record<string, unknown>).pagination as
    { page: number; limit: number; total: number; totalPages: number } | undefined;

  return {
    data: (response.data ?? []) as LocaleKey[],
    pagination: pagination ?? { page: 1, limit: 50, total: 0, totalPages: 0 },
  };
}

/**
 * PUT /api/v1/locale/override?tenantId=<uuid>&lang=<code>
 *
 * Batch-saves 1–500 terminology overrides for a specific tenant and language.
 * ADMIN role required (auth pending on BE).
 *
 * Each key calls upsert_locale_override() atomically — sets custom_value
 * and appends an OVERRIDE audit row. After all upserts, the full resolved
 * bundle is rebuilt from the DB and written to S3. CloudFront is invalidated.
 *
 * Used by:
 *   • Settings > Localization > Terminology Editor (save changes)
 *   • Onboarding > Terminology Review step (submit overrides)
 *
 * After a successful call, the caller should call i18next.reloadResources()
 * so the admin sees the updated terms immediately.
 *
 * @param query - tenantId (UUID) + lang (language code)
 * @param body  - { overrides: [{ key, custom_value, custom_value_plural? }] }
 * @returns Processed count, total, message, and the updated full bundle
 */
export async function saveOverrides(
  query: LocaleOverrideBatchQuery,
  body: LocaleOverrideBody,
): Promise<OverrideResponse> {
  const api = getApiClient();
  const params = new URLSearchParams({
    tenantId: query.tenantId,
    lang: query.lang,
  });

  // createApiClient's response interceptor already unwraps { success, data, meta }
  // → response.data IS the inner payload (OverrideResponse)
  const response = await api.put<OverrideResponse>(
    `${LOCALE_API}/override?${params.toString()}`,
    body,
  );

  return response.data;
}

/**
 * DELETE /api/v1/locale/override/:key
 *
 * Reverts a terminology override, restoring resolved_value to
 * translated_value (or base_value if no translation exists).
 * JWT + ADMIN role required.
 *
 * Calls reset_locale_override() which clears custom_value and
 * appends a RESET audit row. Invalidates the Redis bundle cache.
 *
 * Used by:
 *   • Settings > Localization > Terminology Editor (reset single key)
 *
 * After a successful call, the caller should trigger a bundle rebuild
 * and call i18next.reloadResources().
 *
 * @param key - The translation key to reset (e.g. 'label.feed')
 */
export async function deleteOverride(key: string): Promise<void> {
  const api = getApiClient();
  await api.delete(`${LOCALE_API}/override/${encodeURIComponent(key)}`);
}

/**
 * POST /api/v1/locale/import?tenant_id=<uuid>&language_code=<code>
 *
 * Bulk-imports up to 500 terminology overrides from a CSV file upload.
 * ADMIN role required (auth pending on BE).
 *
 * Only keys already present in locale_strings are updated — unknown keys
 * are silently skipped. After all rows are processed, the full resolved
 * bundle is rebuilt and S3 is updated. CloudFront is invalidated.
 *
 * Used by:
 *   • Settings > Localization > CSV Import dialog
 *   • Onboarding > Bulk terminology import
 *
 * @param tenantId     - Tenant UUID
 * @param languageCode - Language code (e.g. 'en', 'en-CA')
 * @param file         - CSV File object (multipart/form-data, field name: 'file')
 * @returns Processed, skipped, total counts, message, and updated bundle
 */
export async function importOverrides(
  tenantId: string,
  languageCode: string,
  file: File,
): Promise<ImportResponse> {
  const api = getApiClient();
  const params = new URLSearchParams({
    tenant_id: tenantId,
    language_code: languageCode,
  });

  const formData = new FormData();
  formData.append('file', file);

  // createApiClient's response interceptor already unwraps { success, data, meta }
  // → response.data IS the inner payload (ImportResponse)
  const response = await api.post<ImportResponse>(
    `${LOCALE_API}/import?${params.toString()}`,
    formData,
  );

  return response.data;
}

/**
 * POST /api/v1/locale/sync
 *
 * Syncs a language bundle from @tms-phoenix/tms-types into S3 and the
 * locale_strings table. ADMIN role required (auth pending on BE).
 *
 * Behaviour depends on is_initial_setup:
 *   • true  — INSERT new rows (first-time onboarding or adding a new language)
 *   • false — UPDATE translated_value on existing rows (refresh after tms-types update)
 *
 * Used by:
 *   • Onboarding > Language selection (initial tenant setup)
 *   • Admin > Sync translations after a tms-types bundle update
 *
 * @param body - { tenant_id, language_code, is_initial_setup }
 * @returns Sync result with S3 key, rows affected, and total keys
 */
export async function syncBundle(body: LocaleSyncBody): Promise<SyncResponse> {
  const api = getApiClient();
  // createApiClient's response interceptor already unwraps { success, data, meta }
  // → response.data IS the inner payload (SyncResponse)
  const response = await api.post<SyncResponse>(
    `${LOCALE_API}/sync`,
    body,
  );
  return response.data;
}
